import React, { useContext } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import { useDeleteRuleset } from './hooks';
import { AppContext } from '../../App';
import { IRuleset } from '../../types/ruleset-types';

interface RulesetDeleteProps {
  dataPoolId: string;
  ruleset: IRuleset;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const RulesetDelete: React.FC<RulesetDeleteProps> = ({
  dataPoolId,
  ruleset,
  open,
  onClose,
  onSuccess,
}) => {
  const { setToast } = useContext(AppContext);

  const { mutate: deleteRuleset, isPending: isDeleting } = useDeleteRuleset({
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      setToast({
        variant: 'error',
        open: true,
        message: error?.message || 'Failed to delete ruleset',
      });
    },
  });

  const handleDelete = () => {
    deleteRuleset({
      dataPoolId,
      rulesetId: ruleset.id,
    });
  };

  if (!open) {
    return null;
  }

  return (
    <DialogGlynt
      open={open}
      closeModal={onClose}
      width="450px"
      cypressId="dialog-ruleset-delete"
      loading={isDeleting}
    >
      <DialogTitleGlynt
        title="Delete Ruleset"
        closeModal={onClose}
        loading={isDeleting}
      />
      <DialogContentGlynt>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            py: 2,
          }}
        >
          <WarningAmberIcon
            sx={{
              fontSize: 48,
              color: '#f57c00',
              mb: 2,
            }}
          />
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to delete the ruleset:
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
            "{ruleset.label}"
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The ruleset and all its conditions will be permanently removed.
          </Typography>
        </Box>
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleDelete}
        closeModal={onClose}
        cancelCypressId="btn-ruleset-delete-cancel"
        submitCypressId="btn-ruleset-delete-confirm"
        clearCypressId="btn-ruleset-delete-clear"
        submitButtonText="Delete"
        submitLoading={isDeleting}
      />
    </DialogGlynt>
  );
};

export default RulesetDelete;
